import React, { useState } from 'react';
import { exportRecordPDF } from '@/lib/clientExport';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Search, Plus, FileText, AlertTriangle, Clock, CheckCircle2, RefreshCw, Download, Loader2, UserPlus, BookOpen, CalendarClock } from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
  PieChart, Pie, Cell,
} from 'recharts';
import CanAccess from '@/components/ui/CanAccess';
import api from '@/lib/api';
import PageHeader from '@/components/ui/PageHeader';
import FilterBar from '@/components/ui/FilterBar';
import ALCOABadge from '@/components/ui/ALCOABadge';
import DataTable, { Column } from '@/components/ui/DataTable';
import PillBadge from '@/components/ui/PillBadge';
import Modal from '@/components/ui/Modal';
import SideDrawer from '@/components/ui/SideDrawer';
import TagChip from '@/components/ui/TagChip';
import { format, differenceInDays } from 'date-fns';

interface SopVersion {
  version: string;
  effectiveDate: string;
  author: string;
  changeNote: string;
}

interface Sop {
  id: string;
  sopCode: string;
  title: string;
  category: string;
  department: string;
  status: string;
  version: string;
  effectiveDate: string;
  reviewDate: string;
  owner: string;
  lab?: { name: string };
  tags: string[];
  versions?: SopVersion[];
  daysUntilReview?: number;
}

const MOCK_SOPS: Sop[] = [
  // Analytical (5)
  {
    id: 'mock-sop-1', sopCode: 'SOP-QC-001', title: 'HPLC System Operation and Maintenance',
    category: 'Analytical', department: 'Quality Control', status: 'active',
    version: '3.1', effectiveDate: '2024-06-01', reviewDate: '2025-06-01',
    owner: 'Dr. Anjali Mehta', lab: { name: 'Mumbai QC Central' },
    tags: ['HPLC', 'equipment', 'calibration', 'analytical'],
    daysUntilReview: 51,
  },
  {
    id: 'mock-sop-5', sopCode: 'SOP-QC-005', title: 'Dissolution Testing — Apparatus 1 and 2 Operation',
    category: 'Analytical', department: 'Quality Control', status: 'active',
    version: '2.3', effectiveDate: '2023-09-01', reviewDate: '2024-09-01',
    owner: 'Meena Iyer', lab: { name: 'Pune Formulation Lab' },
    tags: ['dissolution', 'USP apparatus', 'analytical', 'QC'],
    daysUntilReview: -224,
  },
  {
    id: 'mock-sop-9', sopCode: 'SOP-QC-009', title: 'GC-MS Method for Residual Solvent Determination (ICH Q3C)',
    category: 'Analytical', department: 'Quality Control', status: 'active',
    version: '1.8', effectiveDate: '2024-01-15', reviewDate: '2025-01-15',
    owner: 'Harish Reddy', lab: { name: 'Hyderabad API Lab' },
    tags: ['GC-MS', 'residual solvents', 'ICH Q3C', 'API'],
    daysUntilReview: -87,
  },
  {
    id: 'mock-sop-13', sopCode: 'SOP-QC-013', title: 'ICP-OES Elemental Analysis — Instrument Calibration and Sample Preparation',
    category: 'Analytical', department: 'Quality Control', status: 'active',
    version: '1.2', effectiveDate: '2025-01-10', reviewDate: '2026-01-10',
    owner: 'Deepak Rao', lab: { name: 'Hyderabad API Lab' },
    tags: ['ICP-OES', 'elemental impurities', 'ICH Q3D', 'metals'],
    daysUntilReview: 274,
  },
  {
    id: 'mock-sop-17', sopCode: 'SOP-RD-017', title: 'Bioanalytical Method Validation — LC-MS/MS in Biological Matrices',
    category: 'Analytical', department: 'R&D', status: 'under_review',
    version: '2.0', effectiveDate: '2022-11-01', reviewDate: '2025-05-01',
    owner: 'Dr. Preeti Joshi', lab: { name: 'Bengaluru R&D Analytical Centre' },
    tags: ['bioanalytical', 'LC-MS/MS', 'plasma', 'EMA guideline'],
    daysUntilReview: 19,
  },
  // Manufacturing (4)
  {
    id: 'mock-sop-2', sopCode: 'SOP-MFG-002', title: 'Tablet Compression — Granulation and Compression Parameters',
    category: 'Manufacturing', department: 'Production', status: 'active',
    version: '2.0', effectiveDate: '2024-03-15', reviewDate: '2025-03-15',
    owner: 'Ramesh Kulkarni', lab: { name: 'Pune Formulation Lab' },
    tags: ['compression', 'granulation', 'manufacturing', 'GMP'],
    daysUntilReview: -28,
  },
  {
    id: 'mock-sop-6', sopCode: 'SOP-MFG-006', title: 'Aseptic Filling and Lyophilisation of Injectable Products',
    category: 'Manufacturing', department: 'Production', status: 'active',
    version: '4.0', effectiveDate: '2023-06-01', reviewDate: '2024-06-01',
    owner: 'Suresh Patil', lab: { name: 'Chennai Sterile Lab' },
    tags: ['aseptic filling', 'lyophilisation', 'sterile manufacturing', 'GMP'],
    daysUntilReview: -315,
  },
  {
    id: 'mock-sop-10', sopCode: 'SOP-MFG-010', title: 'Cleaning Validation for Multi-Product Manufacturing Equipment',
    category: 'Manufacturing', department: 'Production', status: 'active',
    version: '3.2', effectiveDate: '2024-10-01', reviewDate: '2025-10-01',
    owner: 'Kavitha Nair', lab: { name: 'Pune Formulation Lab' },
    tags: ['cleaning validation', 'MACO', 'swab sampling', 'GMP'],
    daysUntilReview: 172,
  },
  {
    id: 'mock-sop-18', sopCode: 'SOP-MFG-018', title: 'Granulation — High-Shear Wet Granulation Process Parameters',
    category: 'Manufacturing', department: 'Production', status: 'archived',
    version: '1.5', effectiveDate: '2022-04-01', reviewDate: '2023-04-01',
    owner: 'Ramesh Kulkarni', lab: { name: 'Pune Formulation Lab' },
    tags: ['granulation', 'wet granulation', 'process parameters', 'scale-up'],
    daysUntilReview: -742,
  },
  // Quality Control (4)
  {
    id: 'mock-sop-3', sopCode: 'SOP-QA-003', title: 'Deviation Management and CAPA Initiation',
    category: 'Quality Control', department: 'Quality Assurance', status: 'active',
    version: '4.2', effectiveDate: '2024-09-01', reviewDate: '2025-09-01',
    owner: 'Priya Nair', lab: { name: 'Mumbai QC Central' },
    tags: ['deviation', 'CAPA', 'QA', 'compliance'],
    daysUntilReview: 142,
  },
  {
    id: 'mock-sop-7', sopCode: 'SOP-QA-007', title: 'Change Control Management — Initiation to Closure',
    category: 'Quality Control', department: 'Quality Assurance', status: 'active',
    version: '3.0', effectiveDate: '2023-03-01', reviewDate: '2024-03-01',
    owner: 'Vijay Sharma', lab: { name: 'Mumbai QC Central' },
    tags: ['change control', 'QA', 'impact assessment', 'GMP'],
    daysUntilReview: -407,
  },
  {
    id: 'mock-sop-11', sopCode: 'SOP-QA-011', title: 'Out-of-Specification (OOS) Investigation Procedure',
    category: 'Quality Control', department: 'Quality Assurance', status: 'active',
    version: '5.2', effectiveDate: '2025-02-01', reviewDate: '2026-02-01',
    owner: 'Dr. Anjali Mehta', lab: { name: 'Mumbai QC Central' },
    tags: ['OOS', 'investigation', 'phase I', 'phase II', 'QA'],
    daysUntilReview: 296,
  },
  {
    id: 'mock-sop-19', sopCode: 'SOP-QA-019', title: 'Annual Product Review — Compilation and Trending',
    category: 'Quality Control', department: 'Quality Assurance', status: 'under_review',
    version: '2.1', effectiveDate: '2023-01-15', reviewDate: '2025-07-15',
    owner: 'Sonal Desai', lab: { name: 'Mumbai QC Central' },
    tags: ['APR', 'product review', 'trending', 'annual'],
    daysUntilReview: 95,
  },
  // Equipment (3)
  {
    id: 'mock-sop-8', sopCode: 'SOP-EQP-008', title: 'Equipment Qualification — IQ/OQ/PQ Protocol for Analytical Instruments',
    category: 'Equipment', department: 'Engineering', status: 'active',
    version: '2.5', effectiveDate: '2024-05-01', reviewDate: '2025-05-01',
    owner: 'Arjun Pillai', lab: { name: 'Bengaluru R&D Analytical Centre' },
    tags: ['IQ', 'OQ', 'PQ', 'qualification', 'validation'],
    daysUntilReview: 19,
  },
  {
    id: 'mock-sop-12', sopCode: 'SOP-EQP-012', title: 'Stability Chamber Operation — Calibration and Alarm Management',
    category: 'Equipment', department: 'Engineering', status: 'active',
    version: '1.9', effectiveDate: '2023-11-01', reviewDate: '2024-11-01',
    owner: 'Harish Reddy', lab: { name: 'Kolkata Stability Chamber Facility' },
    tags: ['stability chamber', 'ICH', 'calibration', 'temperature mapping'],
    daysUntilReview: -162,
  },
  {
    id: 'mock-sop-20', sopCode: 'SOP-EQP-020', title: 'Autoclave Validation and Periodic Requalification (Steam Sterilisation)',
    category: 'Equipment', department: 'Engineering', status: 'active',
    version: '3.0', effectiveDate: '2025-03-01', reviewDate: '2026-03-01',
    owner: 'Suresh Patil', lab: { name: 'Chennai Sterile Lab' },
    tags: ['autoclave', 'steam sterilisation', 'F0 value', 'validation'],
    daysUntilReview: 324,
  },
  // Safety (3)
  {
    id: 'mock-sop-4', sopCode: 'SOP-SAF-004', title: 'Chemical Spill Response and Containment Protocol',
    category: 'Safety', department: 'EHS', status: 'under_review',
    version: '1.5', effectiveDate: '2023-12-01', reviewDate: '2025-05-01',
    owner: 'Arun Menon', lab: { name: 'Chennai Sterile Lab' },
    tags: ['safety', 'spill', 'emergency', 'EHS'],
    daysUntilReview: 19,
  },
  {
    id: 'mock-sop-14', sopCode: 'SOP-SAF-014', title: 'Handling of Cytotoxic and Potent Compounds — OEB 4/5 Protocols',
    category: 'Safety', department: 'EHS', status: 'active',
    version: '2.2', effectiveDate: '2024-07-01', reviewDate: '2025-07-01',
    owner: 'Dr. Preeti Joshi', lab: { name: 'Bengaluru R&D Analytical Centre' },
    tags: ['cytotoxic', 'potent compounds', 'OEB', 'containment', 'PPE'],
    daysUntilReview: 81,
  },
  {
    id: 'mock-sop-21', sopCode: 'SOP-SAF-021', title: 'Biosafety Level 2 Laboratory Work Practices',
    category: 'Safety', department: 'EHS', status: 'active',
    version: '1.0', effectiveDate: '2025-01-01', reviewDate: '2026-01-01',
    owner: 'Kavitha Nair', lab: { name: 'Delhi Contract Microbiology Lab' },
    tags: ['biosafety', 'BSL-2', 'containment', 'PPE', 'microbiological'],
    daysUntilReview: 265,
  },
  // Regulatory (2)
  {
    id: 'mock-sop-15', sopCode: 'SOP-REG-015', title: 'GDP — Good Documentation Practices for GMP Records',
    category: 'Regulatory', department: 'Regulatory Affairs', status: 'active',
    version: '4.1', effectiveDate: '2023-05-01', reviewDate: '2024-05-01',
    owner: 'Vijay Sharma', lab: { name: 'Mumbai QC Central' },
    tags: ['GDP', 'documentation', 'GMP', 'data integrity', 'ALCOA'],
    daysUntilReview: -346,
  },
  {
    id: 'mock-sop-16', sopCode: 'SOP-REG-016', title: 'Regulatory Submission Dossier Preparation — CTD Format',
    category: 'Regulatory', department: 'Regulatory Affairs', status: 'obsolete',
    version: '2.0', effectiveDate: '2022-06-01', reviewDate: '2023-06-01',
    owner: 'Arjun Pillai', lab: { name: 'Mumbai QC Central' },
    tags: ['CTD', 'dossier', 'submission', 'regulatory', 'NDA'],
    daysUntilReview: -681,
  },
  // Administrative (1)
  {
    id: 'mock-sop-22', sopCode: 'SOP-ADM-022', title: 'SOP Lifecycle Management — Drafting, Review, Approval and Archival',
    category: 'Administrative', department: 'Quality Assurance', status: 'active',
    version: '3.5', effectiveDate: '2024-11-01', reviewDate: '2025-11-01',
    owner: 'Priya Nair', lab: { name: 'Mumbai QC Central' },
    tags: ['SOP management', 'document control', 'lifecycle', 'QA'],
    daysUntilReview: 203,
  },
];

const SOP_CATEGORIES = ['Analytical', 'Microbiology', 'Manufacturing', 'Quality Assurance', 'Safety', 'Equipment', 'Regulatory', 'Training', 'Stability', 'Environmental'];

export default function Sop() {
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [labId, setLabId] = useState('');
  const [dateRange, setDateRange] = useState('6m');
  const [page, setPage] = useState(1);
  const [showCreate, setShowCreate] = useState(false);
  const [showVersionModal, setShowVersionModal] = useState(false);
  const [selectedSop, setSelectedSop] = useState<Sop | null>(null);
  const [versionForm, setVersionForm] = useState({ version: '', effectiveDate: '', changeNote: '' });
  const [exporting, setExporting] = useState(false);
  const [form, setForm] = useState({
    title: '', category: '', department: '', labId: '',
    version: '1.0', effectiveDate: '', reviewDate: '', owner: '', tags: '',
  });

  // Training assignment state
  const [showTraining, setShowTraining] = useState<string | null>(null);
  const [trainingForm, setTrainingForm] = useState({ assignees: '', dueDate: '', message: '' });

  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['sops', { search, category: categoryFilter, status: statusFilter, page, labId, dateRange }],
    queryFn: () =>
      api.get('/sops', { params: { search, category: categoryFilter, status: statusFilter, page, limit: 20, ...(labId && { labId }), range: dateRange } })
        .then((r) => r.data),
  });

  const { data: dueForReview } = useQuery({
    queryKey: ['sops-due-review'],
    queryFn: () => api.get('/sops/due-for-review').then((r) => r.data.data),
    staleTime: 300000,
  });

  const { data: sopAnalytics } = useQuery({
    queryKey: ['sop-analytics'],
    queryFn: () => api.get('/sop/analytics').then((r) => r.data.data),
    staleTime: 120000,
  });

  const { data: labs } = useQuery({
    queryKey: ['labs-all'],
    queryFn: () => api.get('/labs/all').then((r) => r.data.data),
  });

  const createMutation = useMutation({
    mutationFn: (payload: typeof form) =>
      api.post('/sops', { ...payload, tags: payload.tags.split(',').map((t) => t.trim()).filter(Boolean) }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sops'] });
      setShowCreate(false);
    },
  });

  const handleExportSop = (id: string) => {
    const s = selectedSop;
    if (!s) return;
    exportRecordPDF(
      `SOP — ${s.sopCode}`,
      s.title,
      [
        ['SOP Code',       s.sopCode],
        ['Title',          s.title],
        ['Version',        `v${s.version}`],
        ['Status',         s.status],
        ['Category',       s.category ?? '—'],
        ['Department',     s.department ?? '—'],
        ['Owner',          s.owner ?? '—'],
        ['Effective Date', new Date(s.effectiveDate).toLocaleDateString('en-IN')],
        ['Review Due',     new Date(s.reviewDate).toLocaleDateString('en-IN')],
        ['Tags',           s.tags?.join(', ') ?? '—'],
      ],
    );
    void id; // id unused — selectedSop already loaded
  };

  const addVersionMutation = useMutation({
    mutationFn: (payload: typeof versionForm) =>
      api.post(`/sops/${selectedSop!.id}/versions`, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sops'] });
      setShowVersionModal(false);
      setVersionForm({ version: '', effectiveDate: '', changeNote: '' });
    },
  });

  // Training assignment mutation
  const trainingMutation = useMutation({
    mutationFn: ({ sopId, payload }: { sopId: string; payload: typeof trainingForm }) =>
      api.post(`/sops/${sopId}/training-assignments`, {
        assignees: payload.assignees
          .split(/[\n,]/)
          .map((s) => s.trim())
          .filter(Boolean),
        dueDate: payload.dueDate,
        message: payload.message,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sops'] });
      setShowTraining(null);
      setTrainingForm({ assignees: '', dueDate: '', message: '' });
    },
  });

  // Mark as reviewed mutation
  const markReviewedMutation = useMutation({
    mutationFn: (id: string) =>
      api.patch(`/sops/${id}`, {
        lastReviewedAt: new Date().toISOString(),
        reviewDue: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString(),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['sops'] }),
  });

  const columns: Column<Record<string, unknown>>[] = [
    { key: 'sopCode', label: 'SOP ID', width: 'w-28',
      render: (row) => <span className="font-mono text-xs text-text-muted">{String(row.sopCode)}</span> },
    { key: 'title', label: 'Title',
      render: (row) => (
        <div>
          <p className="font-medium text-text text-sm">{String(row.title)}</p>
          <p className="text-xs text-text-dim">{String(row.category)} · {String(row.department || '—')}</p>
        </div>
      )},
    { key: 'version', label: 'Ver', width: 'w-16', align: 'center',
      render: (row) => <span className="font-mono text-xs bg-teal/10 text-teal px-2 py-0.5 rounded">v{String(row.version)}</span> },
    { key: 'status', label: 'Status', render: (row) => <PillBadge value={String(row.status)} showDot /> },
    { key: 'reviewDate', label: 'Review Due',
      render: (row) => {
        const dateStr = String(row.reviewDate);
        const days = differenceInDays(new Date(dateStr), new Date());
        const formattedDate = format(new Date(dateStr), 'd MMM yyyy');
        const isOverdue = days < 0;
        const isDueSoon = days >= 0 && days <= 30;
        return (
          <div className="flex flex-col gap-0.5">
            <span className="text-xs font-mono">{formattedDate}</span>
            {isOverdue && (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-risk-high/10 text-risk-high uppercase tracking-wide w-fit">
                Overdue
              </span>
            )}
            {isDueSoon && (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-risk-medium/10 text-risk-medium uppercase tracking-wide w-fit">
                Due Soon
              </span>
            )}
          </div>
        );
      }},
    { key: 'owner', label: 'Owner',
      render: (row) => <span className="text-sm text-text">{String(row.owner || '—')}</span> },
    { key: '_actions', label: '',
      render: (row) => (
        <button
          onClick={(e) => { e.stopPropagation(); setShowTraining(String(row.id)); }}
          className="inline-flex items-center gap-1 text-xs text-text-muted hover:text-teal transition-colors px-2 py-1 rounded hover:bg-teal/5"
          title="Assign Training"
        >
          <UserPlus size={12} />
          <span>Assign</span>
        </button>
      )},
  ];

  return (
    <div className="space-y-5">
      <PageHeader
        title="SOP Management"
        subtitle={`${data?.meta?.total || 0} standard operating procedures`}
        actions={
          <>
            <ALCOABadge />
            <CanAccess roles={['admin', 'qa_director', 'lab_head']}>
              <button onClick={() => setShowCreate(true)} className="btn-primary"><Plus size={13} /> New SOP</button>
            </CanAccess>
          </>
        }
      />

      <FilterBar
        labId={labId} onLabChange={(id) => { setLabId(id); setPage(1); }}
        dateRange={dateRange} onDateRangeChange={(r) => { setDateRange(r); setPage(1); }}
      />

      {/* Due for review alert */}
      {dueForReview && dueForReview.length > 0 && (
        <div className="card p-4 border-l-4 border-risk-medium">
          <div className="flex items-start gap-3">
            <RefreshCw size={15} className="text-risk-medium mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-semibold text-text">{dueForReview.length} SOPs due for review within 90 days</p>
              <div className="flex flex-wrap gap-1.5 mt-2">
                {dueForReview.slice(0, 8).map((s: Sop) => (
                  <button key={s.id} onClick={() => setSelectedSop(s)}
                    className="text-xs bg-risk-medium/10 text-risk-medium px-2 py-0.5 rounded-full hover:bg-risk-medium/20 transition-colors">
                    {s.sopCode}
                  </button>
                ))}
                {dueForReview.length > 8 && (
                  <span className="text-xs text-text-dim self-center">+{dueForReview.length - 8} more</span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── SOP Analytics ──────────────────────────────────────────── */}
      {sopAnalytics && (
        <>
          {/* Stat strip */}
          <div className="grid grid-cols-4 gap-3">
            {[
              { label: 'Total SOPs',       value: sopAnalytics.total,          color: '#2563EB', icon: <BookOpen     size={15} /> },
              { label: 'Active',           value: sopAnalytics.active,         color: '#16A34A', icon: <CheckCircle2  size={15} /> },
              { label: 'Due This Quarter', value: sopAnalytics.dueThisQuarter, color: '#D97706', icon: <CalendarClock size={15} /> },
              { label: 'Overdue Review',   value: sopAnalytics.overdueReview,  color: '#DC2626', icon: <AlertTriangle  size={15} /> },
            ].map((s) => (
              <div key={s.label} className="card p-4 flex items-center gap-3" style={{ borderLeft: `3px solid ${s.color}` }}>
                <div style={{ color: s.color, flexShrink: 0 }}>{s.icon}</div>
                <div>
                  <p className="text-xs text-text-muted mb-0.5">{s.label}</p>
                  <p className="text-xl font-bold font-mono" style={{ color: s.color }}>{s.value}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Charts row */}
          <div className="grid grid-cols-2 gap-4">
            {/* SOP Status Donut */}
            <div className="card p-4">
              <h3 className="text-sm font-semibold text-text mb-1">SOP Status Distribution</h3>
              <p className="text-xs text-text-muted mb-3">Breakdown by lifecycle status</p>
              <div style={{ height: 190 }}>
                <ResponsiveContainer>
                  <PieChart>
                    <Pie
                      data={[
                        { name: 'Active',       value: sopAnalytics.active,      fill: '#16A34A' },
                        { name: 'Under Review', value: sopAnalytics.underReview, fill: '#D97706' },
                        { name: 'Draft',        value: sopAnalytics.draft,       fill: '#2563EB' },
                        { name: 'Retired',      value: sopAnalytics.retired,     fill: '#6B7280' },
                      ].filter(d => d.value > 0)}
                      cx="50%" cy="50%"
                      innerRadius={52} outerRadius={72}
                      paddingAngle={3} dataKey="value" strokeWidth={0}
                    >
                      {[
                        { name: 'Active', fill: '#16A34A' }, { name: 'Under Review', fill: '#D97706' },
                        { name: 'Draft', fill: '#2563EB' },  { name: 'Retired', fill: '#6B7280' },
                      ].map((d, i) => <Cell key={i} fill={d.fill} />)}
                    </Pie>
                    <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                    <Legend iconSize={8} wrapperStyle={{ fontSize: 11 }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* SOPs by Department */}
            <div className="card p-4">
              <h3 className="text-sm font-semibold text-text mb-1">SOPs by Department</h3>
              <p className="text-xs text-text-muted mb-3">Distribution across departments</p>
              <div style={{ height: 190 }}>
                <ResponsiveContainer>
                  <BarChart data={sopAnalytics.byDepartment} layout="vertical" margin={{ top: 4, right: 8, bottom: 4, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" horizontal={false} />
                    <XAxis type="number" tick={{ fontSize: 10, fill: '#94A3B8' }} axisLine={false} tickLine={false} />
                    <YAxis type="category" dataKey="dept" tick={{ fontSize: 10.5, fill: '#5a5a6e' }} width={80} axisLine={false} tickLine={false} />
                    <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                    <Bar dataKey="count" name="SOPs" fill="#7C3AED" radius={[0, 4, 4, 0]} maxBarSize={14} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-48">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-dim" />
          <input placeholder="Search SOPs..." value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }} className="input pl-8" />
        </div>
        <select value={categoryFilter} onChange={(e) => { setCategoryFilter(e.target.value); setPage(1); }} className="select w-40">
          <option value="">All Categories</option>
          {SOP_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }} className="select w-36">
          <option value="">All Status</option>
          <option value="active">Active</option>
          <option value="draft">Draft</option>
          <option value="under_review">Under Review</option>
          <option value="obsolete">Obsolete</option>
        </select>
      </div>

      <DataTable
        columns={columns}
        data={((data?.data && data.data.length > 0) ? data.data : MOCK_SOPS) as Record<string, unknown>[]}
        loading={isLoading}
        onRowClick={(row) => setSelectedSop(row as unknown as Sop)}
        total={data?.meta?.total}
        page={page}
        pageSize={20}
        onPageChange={setPage}
      />

      {/* Detail Drawer */}
      <SideDrawer isOpen={!!selectedSop} onClose={() => setSelectedSop(null)}
        title={selectedSop?.sopCode || ''} subtitle={selectedSop?.title} width={480}>
        {selectedSop && (
          <div className="space-y-5">
            <div className="flex items-center gap-2 flex-wrap">
              <PillBadge value={selectedSop.status} showDot />
              <span className="font-mono text-xs bg-teal/10 text-teal px-2 py-0.5 rounded">v{selectedSop.version}</span>
              {selectedSop.category && <TagChip label={selectedSop.category} color="blue" />}
            </div>

            <div className="grid grid-cols-2 gap-3">
              {[
                { label: 'Effective Date', value: format(new Date(selectedSop.effectiveDate), 'd MMM yyyy') },
                { label: 'Review Due', value: format(new Date(selectedSop.reviewDate), 'd MMM yyyy') },
                { label: 'Owner', value: selectedSop.owner || '—' },
                { label: 'Department', value: selectedSop.department || '—' },
              ].map((item) => (
                <div key={item.label} className="p-3 bg-bg rounded-lg">
                  <p className="text-xs text-text-muted mb-0.5">{item.label}</p>
                  <p className="text-sm font-medium text-text">{item.value}</p>
                </div>
              ))}
            </div>

            {selectedSop.tags && selectedSop.tags.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-text-muted mb-2">Tags</p>
                <div className="flex flex-wrap gap-1.5">
                  {selectedSop.tags.map((t) => <TagChip key={t} label={t} color="gray" />)}
                </div>
              </div>
            )}

            {/* Version history */}
            {selectedSop.versions && selectedSop.versions.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-text-muted mb-2">Version History</p>
                <div className="space-y-2">
                  {selectedSop.versions.map((v) => (
                    <div key={v.version} className="p-2.5 bg-bg rounded-lg flex items-start gap-3">
                      <span className="font-mono text-xs bg-white border border-border text-text-muted px-1.5 py-0.5 rounded flex-shrink-0">v{v.version}</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-text-muted">{format(new Date(v.effectiveDate), 'd MMM yyyy')} · {v.author}</p>
                        <p className="text-xs text-text mt-0.5">{v.changeNote}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Action buttons */}
            <div className="flex gap-2">
              <button onClick={() => handleExportSop(selectedSop.id)} disabled={exporting} className="btn-secondary flex-1 text-sm">
                {exporting ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />} Export PDF
              </button>
              <button onClick={() => setShowVersionModal(true)} className="btn-secondary flex-1 text-sm">
                <Plus size={12} /> Add Version
              </button>
            </div>

            {/* Mark as Reviewed */}
            <div className="pt-1 border-t border-border">
              <button
                onClick={() => {
                  markReviewedMutation.mutate(selectedSop.id);
                }}
                disabled={markReviewedMutation.isPending}
                className="btn-secondary w-full text-sm flex items-center justify-center gap-2"
              >
                {markReviewedMutation.isPending
                  ? <Loader2 size={13} className="animate-spin" />
                  : <CheckCircle2 size={13} className="text-risk-low" />}
                Mark as Reviewed
              </button>
            </div>

            {/* Assign Training */}
            <div>
              <button
                onClick={() => setShowTraining(selectedSop.id)}
                className="btn-secondary w-full text-sm flex items-center justify-center gap-2"
              >
                <UserPlus size={13} />
                Assign Training
              </button>
            </div>
          </div>
        )}
      </SideDrawer>

      {/* Add Version Modal */}
      <Modal isOpen={showVersionModal} onClose={() => setShowVersionModal(false)} title="Add Version"
        subtitle={`New revision for ${selectedSop?.sopCode}`} size="sm"
        footer={
          <>
            <button onClick={() => setShowVersionModal(false)} className="btn-secondary">Cancel</button>
            <button onClick={() => addVersionMutation.mutate(versionForm)} disabled={addVersionMutation.isPending} className="btn-primary">
              {addVersionMutation.isPending ? 'Saving...' : 'Save Version'}
            </button>
          </>
        }
      >
        <div className="space-y-4">
          <div>
            <label className="label">Version Number *</label>
            <input className="input" value={versionForm.version} onChange={(e) => setVersionForm((f) => ({ ...f, version: e.target.value }))} placeholder="e.g. 2.0" />
          </div>
          <div>
            <label className="label">Effective Date *</label>
            <input type="date" className="input" value={versionForm.effectiveDate} onChange={(e) => setVersionForm((f) => ({ ...f, effectiveDate: e.target.value }))} />
          </div>
          <div>
            <label className="label">Change Note *</label>
            <textarea className="input resize-none" rows={3} value={versionForm.changeNote}
              onChange={(e) => setVersionForm((f) => ({ ...f, changeNote: e.target.value }))} placeholder="Describe what changed..." />
          </div>
        </div>
      </Modal>

      {/* Assign Training Modal */}
      <Modal
        isOpen={!!showTraining}
        onClose={() => { setShowTraining(null); setTrainingForm({ assignees: '', dueDate: '', message: '' }); }}
        title="Assign Training"
        subtitle="Assign this SOP for training to team members"
        size="sm"
        footer={
          <>
            <button
              onClick={() => { setShowTraining(null); setTrainingForm({ assignees: '', dueDate: '', message: '' }); }}
              className="btn-secondary"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                if (showTraining) {
                  trainingMutation.mutate({ sopId: showTraining, payload: trainingForm });
                }
              }}
              disabled={trainingMutation.isPending || !trainingForm.assignees.trim() || !trainingForm.dueDate}
              className="btn-primary"
            >
              {trainingMutation.isPending ? 'Assigning...' : 'Assign Training'}
            </button>
          </>
        }
      >
        <div className="space-y-4">
          <div>
            <label className="label">Assignees *</label>
            <textarea
              className="input resize-none"
              rows={4}
              value={trainingForm.assignees}
              onChange={(e) => setTrainingForm((f) => ({ ...f, assignees: e.target.value }))}
              placeholder="One name per line or comma-separated&#10;e.g. John Smith, Jane Doe"
            />
            <p className="text-[11px] text-text-dim mt-1">Enter names separated by commas or new lines.</p>
          </div>
          <div>
            <label className="label">Due Date *</label>
            <input
              type="date"
              className="input"
              value={trainingForm.dueDate}
              onChange={(e) => setTrainingForm((f) => ({ ...f, dueDate: e.target.value }))}
            />
          </div>
          <div>
            <label className="label">Message <span className="text-text-dim font-normal">(optional)</span></label>
            <textarea
              className="input resize-none"
              rows={3}
              value={trainingForm.message}
              onChange={(e) => setTrainingForm((f) => ({ ...f, message: e.target.value }))}
              placeholder="Optional note to assignees..."
            />
          </div>
        </div>
      </Modal>

      {/* Create SOP Modal */}
      <Modal isOpen={showCreate} onClose={() => setShowCreate(false)} title="New SOP"
        subtitle="Create a new standard operating procedure" size="lg"
        footer={
          <>
            <button onClick={() => setShowCreate(false)} className="btn-secondary">Cancel</button>
            <button onClick={() => createMutation.mutate(form)} disabled={createMutation.isPending} className="btn-primary">
              {createMutation.isPending ? 'Creating...' : 'Create SOP'}
            </button>
          </>
        }
      >
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <label className="label">Title *</label>
            <input className="input" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} placeholder="SOP title..." />
          </div>
          <div>
            <label className="label">Category *</label>
            <select className="select" value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}>
              <option value="">Select...</option>
              {SOP_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="label">Department</label>
            <input className="input" value={form.department} onChange={(e) => setForm((f) => ({ ...f, department: e.target.value }))} />
          </div>
          <div>
            <label className="label">Lab</label>
            <select className="select" value={form.labId} onChange={(e) => setForm((f) => ({ ...f, labId: e.target.value }))}>
              <option value="">Select lab...</option>
              {(labs || []).map((l: { id: string; name: string }) => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select>
          </div>
          <div>
            <label className="label">Owner</label>
            <input className="input" value={form.owner} onChange={(e) => setForm((f) => ({ ...f, owner: e.target.value }))} />
          </div>
          <div>
            <label className="label">Effective Date *</label>
            <input type="date" className="input" value={form.effectiveDate} onChange={(e) => setForm((f) => ({ ...f, effectiveDate: e.target.value }))} />
          </div>
          <div>
            <label className="label">Review Date *</label>
            <input type="date" className="input" value={form.reviewDate} onChange={(e) => setForm((f) => ({ ...f, reviewDate: e.target.value }))} />
          </div>
          <div className="col-span-2">
            <label className="label">Tags (comma-separated)</label>
            <input className="input" value={form.tags} onChange={(e) => setForm((f) => ({ ...f, tags: e.target.value }))} placeholder="e.g. HPLC, validation, QC" />
          </div>
        </div>
      </Modal>
    </div>
  );
}
